
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { getProjectsByCategory } from '../../mocks/projects';

export default function ProjectsPage() {
  const [activeTab, setActiveTab] = useState<'backend' | 'frontend' | 'mobile'>('backend');
  const { i18n } = useTranslation();

  const backendProjects = getProjectsByCategory('backend');
  const frontendProjects = getProjectsByCategory('frontend');
  const mobileProjects = getProjectsByCategory('mobile');

  const getCurrentProjects = () => {
    switch (activeTab) {
      case 'backend':
        return backendProjects;
      case 'frontend':
        return frontendProjects;
      case 'mobile':
        return mobileProjects;
      default:
        return backendProjects;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-24 px-6 bg-[#FAFAF8] mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="text-6xl font-black text-[#0A0A0A] leading-tight mb-8">
              {i18n.language === 'fr' ? 'Tous mes' : 'All my'}<br />
              {i18n.language === 'fr' ? 'Projets' : 'Projects'}
            </h1>

            {/* Tabs */}
            <div className="flex gap-3 mb-12">
              <button
                onClick={() => setActiveTab('backend')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'backend'
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-white text-[#0A0A0A] hover:bg-gray-100'
                }`}
              >
                <i className="ri-server-line mr-2"></i>
                Backend ({backendProjects.length})
              </button>
              <button
                onClick={() => setActiveTab('frontend')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'frontend'
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-white text-[#0A0A0A] hover:bg-gray-100'
                }`}
              >
                <i className="ri-layout-line mr-2"></i>
                Frontend ({frontendProjects.length})
              </button>
              <button
                onClick={() => setActiveTab('mobile')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'mobile'
                    ? 'bg-[#0A0A0A] text-white'
                    : 'bg-white text-[#0A0A0A] hover:bg-gray-100'
                }`}
              >
                <i className="ri-smartphone-line mr-2"></i>
                Mobile ({mobileProjects.length})
              </button>
            </div>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {getCurrentProjects().map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group block"
              >
                {/* Image */}
                <div className="relative w-full h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className={`absolute top-6 right-6 ${project.statusColor} text-white px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap shadow-lg`}>
                    {project.status}
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#0A0A0A] mb-4 leading-tight">
                    {project.title}
                  </h3>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {project.tech.slice(0, 5).map((tech, i) => (
                      <span
                        key={i}
                        className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.tech.length > 5 && (
                      <span className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap">
                        +{project.tech.length - 5}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-line"></i>
                      <span>{project.period}</span>
                    </div>
                    {project.manager && (
                      <div className="flex items-center gap-2">
                        <i className="ri-user-line"></i>
                        <span className="truncate">{project.manager}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed mb-6">
                    {project.description}
                  </p>

                  <div className="flex items-center gap-2 text-[#0066FF] text-sm font-semibold group-hover:gap-3 transition-all pt-4 border-t border-gray-100">
                    <span>{i18n.language === 'fr' ? 'Voir les détails' : 'View details'}</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
